import { useEffect, useState } from "react";
import { Loader2, Sparkles, Eye, EyeOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const EMPTY = {
  email: "",
  password: "",
  full_name: "",
  phone: "+33",
  role: "agent",
  notifications_enabled: true,
  is_active: true,
};

function generatePassword() {
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const sym = "!@#$%^&*";
  let p = "";
  for (let i = 0; i < 10; i += 1)
    p += chars[Math.floor(Math.random() * chars.length)];
  p += sym[Math.floor(Math.random() * sym.length)];
  // ensure uppercase + digit
  p += "A1";
  return p
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");
}

export default function UserFormDialog({
  mode = "create", // "create" | "edit"
  open,
  onOpenChange,
  initial = null,
  isSelf = false,
  onSubmit,
  busy = false,
}) {
  const [form, setForm] = useState(EMPTY);
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initial) {
      setForm({
        email: initial.email || "",
        password: "",
        full_name: initial.full_name || "",
        phone: initial.phone || "+33",
        role: initial.role || "agent",
        notifications_enabled: initial.notifications_enabled !== false,
        is_active: initial.is_active !== false,
      });
    } else {
      setForm({ ...EMPTY, password: generatePassword() });
    }
    setErrors({});
  }, [open, mode, initial]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Email invalide";
    if (!form.full_name || form.full_name.trim().length < 2)
      e.full_name = "Nom requis";
    if (!/^\+\d{8,15}$/.test(form.phone))
      e.phone = "Format E.164 attendu (ex: +33685424054)";
    if (!["admin", "agent"].includes(form.role)) e.role = "Rôle invalide";
    if (mode === "create" && (!form.password || form.password.length < 8))
      e.password = "Mot de passe ≥ 8 caractères";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onConfirm = () => {
    if (!validate()) return;
    const payload = {
      email: form.email.trim().toLowerCase(),
      full_name: form.full_name.trim(),
      phone: form.phone.trim(),
      role: form.role,
      notifications_enabled: form.notifications_enabled,
    };
    if (mode === "create") {
      payload.password = form.password;
    } else {
      payload.is_active = form.is_active;
    }
    onSubmit(payload);
  };

  const isCreate = mode === "create";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={isCreate ? "user-create-dialog" : "user-edit-dialog"}
        className="max-w-[480px] rounded-[20px]"
      >
        <DialogHeader>
          <DialogTitle className="text-[20px] font-bold text-slate-900">
            {isCreate ? "Nouvel utilisateur" : "Modifier l'utilisateur"}
          </DialogTitle>
          <DialogDescription className="mt-0.5 text-[13px] text-slate-500">
            {isCreate
              ? "L'utilisateur recevra ses identifiants par vos soins."
              : "Mettez à jour le profil et le statut du compte."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <Field
            label="Email"
            error={errors.email}
            input={
              <input
                type="email"
                data-testid="user-form-email"
                className="sa-input"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                autoComplete="email"
              />
            }
          />
          <Field
            label="Nom complet"
            error={errors.full_name}
            input={
              <input
                type="text"
                data-testid="user-form-name"
                className="sa-input"
                value={form.full_name}
                onChange={(e) => update("full_name", e.target.value)}
              />
            }
          />
          <Field
            label="Téléphone (E.164)"
            error={errors.phone}
            input={
              <input
                type="tel"
                data-testid="user-form-phone"
                className="sa-input"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+33685424054"
              />
            }
          />

          {isCreate ? (
            <Field
              label="Mot de passe initial"
              error={errors.password}
              input={
                <div className="flex items-stretch gap-2">
                  <div className="relative flex-1">
                    <input
                      type={showPw ? "text" : "password"}
                      data-testid="user-form-password"
                      className="sa-input pr-10"
                      value={form.password}
                      onChange={(e) => update("password", e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      tabIndex={-1}
                    >
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  <button
                    type="button"
                    data-testid="user-form-generate-password"
                    onClick={() => update("password", generatePassword())}
                    className="inline-flex shrink-0 items-center gap-1 rounded-[12px] border border-slate-200 bg-white px-3 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <Sparkles size={12} /> Générer
                  </button>
                </div>
              }
            />
          ) : null}

          <Field
            label="Rôle"
            error={errors.role}
            input={
              <div className="flex items-stretch gap-2">
                {["agent", "admin"].map((r) => (
                  <button
                    type="button"
                    key={r}
                    data-testid={`user-form-role-${r}`}
                    onClick={() => !isSelf && update("role", r)}
                    disabled={isSelf}
                    className={`flex-1 rounded-[12px] border px-3 py-2.5 text-[13px] font-semibold transition disabled:opacity-50 ${
                      form.role === r
                        ? "border-[var(--sa-blue)] bg-[var(--sa-blue-soft)] text-[var(--sa-blue-dark)]"
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {r === "admin" ? "Administrateur" : "Agent"}
                  </button>
                ))}
              </div>
            }
            hint={isSelf ? "Vous ne pouvez pas modifier votre propre rôle." : null}
          />

          <ToggleField
            label="Notifications email + SMS"
            on={form.notifications_enabled}
            onChange={(v) => update("notifications_enabled", v)}
            testId="user-form-notif"
          />

          {!isCreate ? (
            <ToggleField
              label="Compte actif"
              on={form.is_active}
              onChange={(v) => !isSelf && update("is_active", v)}
              disabled={isSelf}
              testId="user-form-active"
              hint={isSelf ? "Vous ne pouvez pas désactiver votre propre compte." : null}
            />
          ) : null}
        </div>

        <DialogFooter className="mt-2 flex gap-2 sm:gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="user-form-cancel"
            className="flex-1 rounded-[14px] border border-slate-200 bg-white px-5 py-3 text-[14px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="user-form-submit"
            className="flex-1 rounded-[14px] bg-[var(--sa-blue)] px-5 py-3 text-[14px] font-bold text-white hover:bg-[var(--sa-blue-dark)] disabled:opacity-70"
          >
            {busy ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                Enregistrement…
              </span>
            ) : isCreate ? (
              "Créer le compte"
            ) : (
              "Enregistrer"
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, input, error, hint }) {
  return (
    <div>
      <label className="sa-label">{label}</label>
      {input}
      {error ? (
        <p className="mt-0.5 text-[11.5px] font-medium text-red-600">{error}</p>
      ) : hint ? (
        <p className="mt-0.5 text-[11.5px] text-slate-400">{hint}</p>
      ) : null}
    </div>
  );
}

function ToggleField({ label, on, onChange, disabled, testId, hint }) {
  return (
    <div>
      <button
        type="button"
        data-testid={testId}
        onClick={() => !disabled && onChange(!on)}
        disabled={disabled}
        className={`flex w-full items-center justify-between rounded-[12px] border px-3 py-2.5 text-left transition ${
          disabled
            ? "cursor-not-allowed opacity-60 border-slate-200 bg-white"
            : "border-slate-200 bg-white hover:bg-slate-50"
        }`}
      >
        <span className="text-[13px] font-semibold text-slate-800">{label}</span>
        <span
          aria-hidden
          className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${
            on ? "bg-[var(--sa-blue)]" : "bg-slate-200"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
              on ? "translate-x-5" : "translate-x-0.5"
            }`}
          />
        </span>
      </button>
      {hint ? (
        <p className="mt-0.5 text-[11.5px] text-slate-400">{hint}</p>
      ) : null}
    </div>
  );
}
